import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { cleanRoomSelection } from "redux/actions";
import MainModal from "components/ModalMain";
import CheckBox from "components/Checkbox";
import Button from "components/Button";
import { useMutation } from "@apollo/client";
import { UPDATE_BOOKING_ROOM_BY_BOOKING_ID_AND_ROOM_ID } from "graphql/service";
import { Head, Container, ChangeDetailsContainer, Footer } from "./styles";
import { debouncedErrorMessage } from "utils/messageUtil";
import { message } from "antd";
import { dateDiff } from "utils/util";

const ModalRoomChange = ({ hidden, buttonStyle }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const modalIsOpen = useSelector((state) => state.selectReducer.modalIsOpen);
  const currentRoom = useSelector((state) => state.selectReducer.currentRoom);
  const selectedRoom = useSelector((state) => state.selectReducer.selectedRoom);
  const [waiveUpgradeFee, setWaiveUpgradeFee] = useState(false);
  const [updateBookingRoomByBookingIdAndRoomId, { loading }] = useMutation(
    UPDATE_BOOKING_ROOM_BY_BOOKING_ID_AND_ROOM_ID,
    {
      onCompleted: (data) => {
        if (data.updateRoomBooking) {
          dispatch(cleanRoomSelection());
          history.push(`/booking-details/${currentRoom?.groupId}`);
          message.success("Room changed successfully");
        } else {
          const content = "Failed to change room";
          debouncedErrorMessage(content);
        }
      },
      onError: (error) => {
        if (!error.networkError) {
          let content = error.message;
          if (error.message.includes("Validation")) {
            content = JSON.stringify(
              error.graphQLErrors[0]?.extensions?.exception
            );
          }
          debouncedErrorMessage(content);
        }
      },
    }
  );

  useEffect(() => {
    return () => {
      dispatch(cleanRoomSelection());
    }
  }, [dispatch])

  const nights = dateDiff(currentRoom?.startDate, currentRoom?.endDate);

  const priceDifference =
    (selectedRoom?.roomType.pricePerNight - currentRoom?.pricePerNight) *
      nights || 0;

  const handleRoomChange = () => {
    if (loading) return;
    updateBookingRoomByBookingIdAndRoomId({
      variables: {
        input: {
          bookingId: currentRoom?.bookingId,
          roomId: selectedRoom?.roomId,
          waiveUpgradeFee: waiveUpgradeFee,
        }
      },
    });
  };

  return (
    <MainModal
      isOpen={modalIsOpen}
      setClose={() => dispatch(cleanRoomSelection())}
      className="modal-success"
      buttonStyle={buttonStyle}
      hidden={hidden}
      additionalstyle={`
        max-width: 500px;
        .modal-content{        
          .close {
            z-index: 200;
          }
          .modal-header {
            position: relative;
            top: 10px;
          }              
          .modal-body {
            height: 100%;
            padding: 24px;
          }
        }
        top: 69px;
      `}
    >
      <Head>Do you want to move the guest to this room?</Head>
      <Container>
        <ChangeDetailsContainer>
          <thead>
            <tr>
              <th className="title"></th>
              <th className="currentRoom">CURRENT ROOM</th>
              <th className="selectedRoom">SELECTED ROOM</th>
            </tr>
          </thead>
          <tbody>
            <tr className="roomType">
              <td className="description">Room Type</td>
              <td className="currentRoom">
                {currentRoom?.roomType?.name +
                  (currentRoom?.barrierFree ? " (Barrier Free)" : "")}
              </td>
              <td className="selectedRoom">
                {selectedRoom?.roomType?.name +
                  (selectedRoom?.barrierFree ? " (Barrier Free)" : "")}
              </td>
            </tr>
            <tr className="roomNumber">
              <td className="description">Room Number</td>
              <td className="currentRoom">{currentRoom?.roomNumber}</td>
              <td className="selectedRoom">{selectedRoom?.roomNumber}</td>
            </tr>
            <tr className="nightlyPrice">
              <td className="description">Nightly Price</td>
              <td className="currentRoom">
                {"$" + currentRoom?.pricePerNight.toFixed(2)}
              </td>
              <td className="selectedRoom">
                {"$" + selectedRoom?.roomType?.pricePerNight?.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </ChangeDetailsContainer>
        <div className="priceDifference">
          <span>{`This room change will modify the nightly rate by ${priceDifference < 0 ? ("-$" + (-priceDifference).toFixed(2)) : ("$"+ priceDifference.toFixed(2))}.`}</span>
        </div>
        {currentRoom?.barrierFree && !selectedRoom?.barrierFree &&
          <div className="warning">
            <span>{`Warning: You are about to change a barrier-free room to a non-barrier-free room.`}</span>
          </div>
        }
        <Footer>
          <CheckBox
            checked={waiveUpgradeFee}
            onChange={() => setWaiveUpgradeFee(!waiveUpgradeFee)}
            title="Upgrade fee waived for this room"
            additionalstyle={`
              margin: 0 0 0 2px;
            `}
          />
          <div className="buttonContainer">
            <Button
              danger
              additionalstyle={`
                width: 79px;
              `}
              onClick={() => dispatch(cleanRoomSelection())}
            >
              Cancel
            </Button>
            <Button
              success
              onClick={handleRoomChange}
              data-loading={loading}
              additionalstyle={`
                width: 71px;
                margin-left: 16px;
              `}
            >
              {loading ? null : "Move"}
            </Button>
          </div>
        </Footer>
      </Container>
    </MainModal>
  );
};

export default ModalRoomChange;
